// @ts-strict-ignore
import isBoolean from 'lodash/isBoolean';
import moment, {Moment} from 'moment';

import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {
  END_OF_TODAY,
  END_OF_YESTERDAY,
  isPeriodToDate,
  isPreviousPeriodPreset,
  START_OF_PREVIOUS_QUARTER,
  START_OF_PREVIOUS_YEAR,
  START_OF_QUARTER,
  START_OF_TODAY,
  START_OF_YEAR,
  START_OF_YESTERDAY,
} from 'common/widgets/date-range-picker/models/presets';
import {BaseDate, DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {UNITS} from 'common/util/date';
import {TimeClause as BookmarkTimeClause} from 'common/report/insights/util/types';
import {DATASET_MIXPANEL} from 'common/report/constants';
import {XAU_MATH_TYPES} from 'common/types/reports/bookmark';
import {reporter} from 'common/report/error-reporting';

import {dateRangeAttrsFromTimeClause, RANGES, TimeClause} from '../models/time-clause';

export function getBookmarkTimeClauseForDateRange(dateRange: DateRange): BookmarkTimeClause {
  const exclusionOffset = dateRange.exclusionOffset;

  const isPtd = isPeriodToDate(dateRange);
  if (isPtd || isPreviousPeriodPreset(dateRange)) {
    const dateRangeType = isPtd ? DateRangeType.Since : DateRangeType.Between;

    let value: string | [BaseDate] | [BaseDate, BaseDate] = isPtd
      ? ([dateRange.from] as [BaseDate])
      : ([dateRange.from, dateRange.to] as [BaseDate, BaseDate]);
    if (dateRange.from === START_OF_TODAY) {
      // Insights has long relied on a fragile exceptional pattern where ONLY `$start_of_current_day` is passed as a raw string
      // literal where all other date values are wrapped in an array.
      value = dateRange.from as string;
    }

    let unit = TimeUnit.Day;
    if (dateRange.from === START_OF_TODAY || dateRange.from === START_OF_YESTERDAY) {
      unit = TimeUnit.Hour;
    } else if (dateRange.from === START_OF_QUARTER || dateRange.from === START_OF_PREVIOUS_QUARTER) {
      unit = TimeUnit.Week;
    } else if (dateRange.from === START_OF_YEAR || dateRange.from === START_OF_PREVIOUS_YEAR) {
      unit = TimeUnit.Month;
    }

    return {
      dataset: DATASET_MIXPANEL,
      dateRangeType,
      value,
      window: null,
      unit,
      exclusionOffset,
    };
  }

  const {type, from, to, window} = dateRange;
  const value = (() => {
    switch (type) {
      case DateRangeType.Since:
        return [from] as [BaseDate];
      case DateRangeType.On:
        return [from, from] as [BaseDate, BaseDate];
      case DateRangeType.Between:
        return [from, to] as [BaseDate, BaseDate];
    }
    return null;
  })();

  const unit = ((): TimeUnit => {
    if (type === DateRangeType.On) {
      return TimeUnit.Hour;
    }
    if (type === DateRangeType.Between || type === DateRangeType.Since) {
      if (dateRange.getIntervalDays() < 4) {
        return TimeUnit.Hour;
      } else if (dateRange.getIntervalDays() < 90) {
        return TimeUnit.Day;
      }
      return TimeUnit.Month;
    }

    if (type === DateRangeType.RelativeAfter) {
      // when a single unit is selected, avoid displaying
      // a single data point by breaking down to a smaller unit
      if (window.value === 1) {
        switch (window.unit) {
          case TimeUnit.Hour:
            return TimeUnit.Minute;
          case TimeUnit.Day:
            return TimeUnit.Hour;
          case TimeUnit.Week:
            return TimeUnit.Day;
          case TimeUnit.Month:
            return TimeUnit.Week;
        }
      }
      return window.unit;
    }
    return TimeUnit.Day;
  })();
  return {
    ...(isBoolean(dateRange.rolling) ? {rolling: dateRange.rolling} : {}),
    dataset: DATASET_MIXPANEL,
    dateRangeType: type,
    unit,
    value,
    exclusionOffset,
    ...(window ? {window} : {}),
  };
}

/**
 * Transforms TimeSection into DateRange
 */
export function getDateRange(timeClauses: TimeClause[]): DateRange {
  if (!timeClauses.length) {
    return new DateRange();
  }
  const clause = timeClauses[0];
  if (clause.range === RANGES.TODAY) {
    return new DateRange({
      type: DateRangeType.Between,
      from: START_OF_TODAY,
      to: END_OF_TODAY,
      exclusionOffset: clause.exclusionOffset,
    });
  } else if (clause.range === RANGES.YESTERDAY) {
    return new DateRange({
      type: DateRangeType.Between,
      from: START_OF_YESTERDAY,
      to: END_OF_YESTERDAY,
      exclusionOffset: clause.exclusionOffset,
    });
  }

  return dateRangeAttrsFromTimeClause(clause);
}

/**
 * For a given timeseries data point, calculates the relative date range for the XAU math operators
 */
export function timestampToXauDateRange(
  timestamp: number,
  {unit, operator, isLastInterval = false, lastIntervalToDate = null},
) {
  const MAU_DAYS = 30;
  const WAU_DAYS = 7;
  // These are the only units allowed in XAU queries, and the only ones this function handles
  const ALLOWED_UNITS = [UNITS[`day`], UNITS[`week`], UNITS[`month`]];
  if (!ALLOWED_UNITS.includes(unit)) {
    reporter.error(new Error(`Unit not in ${ALLOWED_UNITS}: "${unit}"`));
  }

  const mToDate = moment(lastIntervalToDate);
  const mIntervalStartDate = moment(timestamp);
  // Calculate the end date for the date range:
  // timestamp + 6 days (for week because range is inclusive) or end of month, unless > lastIntervalToDate
  let toDate: Moment;
  if (unit === UNITS[`month`]) {
    toDate = isLastInterval ? mToDate : moment(mIntervalStartDate).endOf(`month`);
  } else if (unit === UNITS[`week`]) {
    toDate = isLastInterval ? mToDate : moment(mIntervalStartDate).add(WAU_DAYS - 1, `days`);
  } else if (unit === UNITS[`day`]) {
    toDate = mIntervalStartDate;
  }

  let fromDate: Moment;
  if (operator === XAU_MATH_TYPES.MAU) {
    fromDate = moment(toDate).subtract(MAU_DAYS - 1, `days`);
  } else if (operator === XAU_MATH_TYPES.WAU) {
    fromDate = moment(toDate).subtract(WAU_DAYS - 1, `days`);
  }

  return {fromDate, toDate};
}
